import secureApi from "../../utils/secureApi";
import { PLAYER_VAULT_ENDPOINTS } from "./endpoints";
import type {
  ClaimRequest,
  ClaimRequestListParams,
  ClaimRequestListResponse,
  ApproveClaimRequestResponse,
  RejectClaimRequestResponse,
} from "./types";

const ENDPOINTS = PLAYER_VAULT_ENDPOINTS.CLAIM_REQUESTS;

export async function listClaimRequests(
  params: ClaimRequestListParams = {}
): Promise<ClaimRequestListResponse> {
  const queryParams = new URLSearchParams();
  if (params.status) {
    queryParams.append("status", params.status);
  }
  queryParams.append("page", (params.pageNum ?? 1).toString());
  queryParams.append("limit", (params.pageSize ?? 20).toString());

  const endpoint = `${ENDPOINTS.LIST}?${queryParams.toString()}`;
  const response = await secureApi.get(endpoint);
  const data = response?.data ?? response;
  const pagination = data?.pagination;

  const rawList: any[] = data?.requests ?? [];

  const results: ClaimRequest[] = rawList.map((item: any): ClaimRequest => ({
    request_id: item.request_id ?? "",
    player_id: item.player_id ?? "",
    player_name: item.player_name ?? "",
    player_vault_id: item.player_vault_id != null ? String(item.player_vault_id) : "",
    requested_by_user_id: item.requested_by_user_id ?? "",
    requested_by_user_phone: item.requested_by_user_phone ?? "",
    relationship_type: item.relationship_type ?? "",
    verification_note: item.verification_note ?? "",
    status: item.status ?? "pending",
    reviewed_by_admin_id: item.reviewed_by_admin_id ?? "",
    rejection_reason: item.rejection_reason ?? "",
    created_at: item.created_at ?? "",
    updated_at: item.updated_at ?? "",
  }));

  const pageSize = params.pageSize ?? 20;
  const totalCount: number = pagination?.total_entries ?? results.length;
  const currentPage: number = pagination?.page ?? params.pageNum ?? 1;
  const totalPages: number = Math.max(1, Math.ceil(totalCount / pageSize));

  return { results, totalCount, currentPage, totalPages };
}

export async function approveClaimRequest(
  id: string
): Promise<ApproveClaimRequestResponse> {
  try {
    const endpoint = ENDPOINTS.APPROVE.replace("{id}", id);
    const response = await secureApi.post(endpoint);
    const data = response?.data ?? response;
    return {
      request_id: data.request_id ?? id,
      player_id: data.player_id ?? "",
      user_id: data.user_id ?? "",
      status: data.status ?? "approved",
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
        error?.message ??
        "Failed to approve claim request."
    );
  }
}

export async function rejectClaimRequest(
  id: string,
  reason: string
): Promise<RejectClaimRequestResponse> {
  try {
    const endpoint = ENDPOINTS.REJECT.replace("{id}", id);
    const response = await secureApi.post(endpoint, { reason });
    const data = response?.data ?? response;
    return {
      request_id: data.request_id ?? id,
      status: data.status ?? "rejected",
    };
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message ??
        error?.message ??
        "Failed to reject claim request."
    );
  }
}
